import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { resolveImg } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/Layout";
import TrackCard from "@/components/TrackCard";
import PostCard from "@/components/PostCard";
import PurchaseModal from "@/components/PurchaseModal";
import {
  Pencil, Plus, Radio, X, Loader2, Calendar, BarChart3,
  Camera, Image as ImageIcon, Music, Trophy, ChevronDown, Bell, BellOff,
} from "lucide-react";
import { toast } from "sonner";
import { trackPublish } from "@/lib/fbPixel";
import { tr } from "@/lib/translate";
import FileUploader from "@/components/FileUploader";
import SuggestedArtists from "@/components/SuggestedArtists";
import DeleteAccountPanel from "@/components/DeleteAccountPanel";
import BlockedUsersPanel from "@/components/BlockedUsersPanel";
import ChangePasswordPanel from "@/components/ChangePasswordPanel";
import ReportMenu from "@/components/ReportMenu";
import ReleaseCard from "@/components/ReleaseCard";
import PriceEquivalents from "@/components/PriceEquivalents";

const COVER_FALLBACK = "https://images.unsplash.com/photo-1429962714451-bb934ecdc4ec?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwzfHxjb25jZXJ0JTIwY3Jvd2QlMjBuaWdodHxlbnwwfHx8fDE3Nzg5ODIwMzR8MA&ixlib=rb-4.1.0&q=85";

export default function ProfilePage() {
  const { id } = useParams();
  const { t } = useI18n();
  const navigate = useNavigate();
  const { user: me, refresh } = useAuth();
  const [profile, setProfile] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [posts, setPosts] = useState([]);
  const [events, setEvents] = useState([]);
  const [tab, setTab] = useState("posts");
  const [editing, setEditing] = useState(false);
  const [edit, setEdit] = useState({ name: "", bio: "" });
  const [showTrackForm, setShowTrackForm] = useState(false);
  const [releases, setReleases] = useState([]);
  const [showReleaseForm, setShowReleaseForm] = useState(false);
  const [showLiveForm, setShowLiveForm] = useState(false);
  const [showAgenda, setShowAgenda] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [buyTrack, setBuyTrack] = useState(null);
  const [activeLive, setActiveLive] = useState(null);
  const [followStats, setFollowStats] = useState({ followers: 0, following: 0, is_following: false });
  const [followBusy, setFollowBusy] = useState(false);
  const [reminder, setReminder] = useState({ subscribed: false, count: 0 });
  const [reminderBusy, setReminderBusy] = useState(false);
  const [savingMedia, setSavingMedia] = useState(false);
  const coverInputRef = useRef(null);
  const avatarInputRef = useRef(null);

  const isOwn = me && me.id === id;
  const isArtist = profile?.role === "artist";

  const loadAll = async () => {
    const [pr, tr, ps, ev, lv, st, rl] = await Promise.all([
      api.get(`/users/${id}`),
      api.get(`/tracks?artist_id=${id}`),
      api.get(`/posts?user_id=${id}`),
      api.get(`/events?artist_id=${id}`),
      api.get(`/lives`),
      api.get(`/users/${id}/stats`),
      api.get(`/releases?artist_id=${id}`).catch(() => ({ data: [] })),
    ]);
    setProfile(pr.data);
    setTracks(tr.data);
    setReleases(rl.data || []);
    setPosts(ps.data);
    setEvents(ev.data);
    setActiveLive(lv.data.find((l) => l.artist_id === id) || null);
    setFollowStats(st.data);
    setEdit({ name: pr.data.name, bio: pr.data.bio || "" });
  };

  useEffect(() => { loadAll(); setTab("posts"); /* eslint-disable-next-line */ }, [id]);

  const loadReminder = async () => {
    if (!me || isOwn || !isArtist) return;
    try {
      const { data } = await api.get(`/artists/${id}/live-reminder`);
      setReminder(data);
    } catch { /* ignore */ }
  };

  useEffect(() => { loadReminder(); /* eslint-disable-next-line */ }, [id, me?.id, profile?.role]);

  if (!profile) return <div className="text-center text-zinc-500 py-12">{t.common.loading}</div>;

  const saveProfile = async () => {
    try {
      await api.patch("/auth/me", edit);
      setEditing(false);
      refresh();
      loadAll();
      toast.success(tr("Profile updated"));
    } catch { toast.error(tr("Could not save")); }
  };

  const toggleFollow = async () => {
    if (!me) { toast.error(tr("Sign in to interact")); return; }
    setFollowBusy(true);
    try {
      const { data } = await api.post(`/users/${id}/follow`);
      setFollowStats((s) => ({ ...s, is_following: data.following, followers: data.followers }));
    } catch (e) { toast.error(tr(e.response?.data?.detail || "Could not save")); }
    finally { setFollowBusy(false); }
  };

  const toggleReminder = async () => {
    if (!me) { toast.error(tr("Sign in to interact")); return; }
    setReminderBusy(true);
    try {
      const { data } = await api.post(`/artists/${id}/live-reminder`);
      setReminder((r) => ({ subscribed: data.subscribed, count: (r.count || 0) + (data.subscribed ? 1 : -1) }));
      toast.success(data.subscribed ? tr("Live reminder ON — vous serez notifié(e)") : tr("Live reminder OFF"));
    } catch (e) { toast.error(tr(e.response?.data?.detail || "Could not save")); }
    finally { setReminderBusy(false); }
  };

  const uploadMedia = async (file, field /* 'cover' | 'avatar' */) => {
    if (!file) return;
    setSavingMedia(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.patch("/auth/me", { [field]: data.url });
      refresh();
      loadAll();
      toast.success(tr("Profile updated"));
    } catch { toast.error(tr("Could not save")); }
    finally { setSavingMedia(false); }
  };

  return (
    <div className="max-w-5xl mx-auto" data-testid="profile-page">
      {/* Cover (clickable for own profile) */}
      <button
        type="button"
        onClick={() => isOwn && coverInputRef.current?.click()}
        className={`relative block w-full h-44 sm:h-64 overflow-hidden ${isOwn ? "cursor-pointer group" : "cursor-default"}`}
        aria-label="cover"
        data-testid="cover-area"
      >
        <img src={profile.cover ? resolveImg(profile.cover) : COVER_FALLBACK} alt="" className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.02]" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/60 to-transparent" />
        {isOwn && (
          <span className="absolute top-3 right-3 inline-flex items-center gap-1.5 bg-black/60 backdrop-blur-sm border border-white/15 rounded-full px-3 py-1.5 text-[11px] text-white opacity-0 group-hover:opacity-100 transition" data-testid="cover-edit-hint">
            <Camera className="w-3.5 h-3.5" /> {t.profile.editCover ?? "Modifier la couverture"}
          </span>
        )}
      </button>
      {isOwn && (
        <input
          ref={coverInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => uploadMedia(e.target.files?.[0], "cover")}
          data-testid="cover-input"
        />
      )}

      {/* Header centered */}
      <div className="px-4 sm:px-6 -mt-14 sm:-mt-16 relative">
        <div className="flex flex-col items-center text-center gap-3">
          {/* Avatar centered with ring (LIVE ⇒ red pulsing, else gradient) */}
          <div className="relative">
            <button
              type="button"
              onClick={() => {
                if (isOwn) { avatarInputRef.current?.click(); return; }
                if (activeLive) navigate(`/lives?start=${activeLive.id}`);
              }}
              className={`rounded-full p-[3px] ${activeLive ? "bg-[#FF0050] shadow-[0_0_24px_rgba(255,0,80,0.55)]" : "bg-gradient-to-br from-[#FF0050] via-[#8A2BE2] to-[#00F2FE]"} ${isOwn || activeLive ? "cursor-pointer" : "cursor-default"}`}
              aria-label="avatar"
              data-testid="avatar-area"
            >
              <div className="rounded-full bg-[#050505] p-[3px]">
                <Avatar src={profile.avatar} name={profile.name} size="lg" />
              </div>
            </button>
            {activeLive && (
              <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 inline-flex items-center bg-[#FF0050] text-white text-[10px] font-black px-2 py-0.5 rounded-full tracking-widest border-2 border-[#050505]" data-testid="profile-live-badge">
                LIVE
              </span>
            )}
            {isOwn && (
              <input
                ref={avatarInputRef}
                type="file"
                accept="image/*"
                capture="user"
                className="hidden"
                onChange={(e) => uploadMedia(e.target.files?.[0], "avatar")}
                data-testid="avatar-input"
              />
            )}
          </div>

          {/* Name + tiny pencil (replaces the big "Modifier" button) */}
          <div className="mt-1 flex items-center gap-2">
            <h1 className="text-3xl sm:text-4xl font-black font-display text-white leading-none" data-testid="profile-name">{profile.name}</h1>
            {isOwn && (
              <button
                onClick={() => setEditing(true)}
                className="p-1.5 rounded-full bg-white/5 hover:bg-white/10 text-zinc-400 hover:text-white transition"
                aria-label="edit-profile"
                title={t.profile.edit}
                data-testid="edit-profile-btn"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {!isOwn && (
            <ReportMenu
              targetType="user"
              targetId={profile.id}
              authorId={profile.id}
              authorName={profile.name}
              onBlocked={() => navigate("/")}
              className="-ml-2"
            />
          )}

          {profile.bio && <p className="text-zinc-400 max-w-xl text-sm">{profile.bio}</p>}

          <div className="flex items-center gap-4 text-sm">
            <span className="text-white font-bold" data-testid="profile-followers">{followStats.followers}</span>
            <span className="text-zinc-500 -ml-3">{t.profile.followers}</span>
            <span className="text-white font-bold" data-testid="profile-following">{followStats.following}</span>
            <span className="text-zinc-500 -ml-3">{t.profile.followingLabel}</span>
          </div>

          {/* Action bar */}
          <div className="flex gap-2 flex-wrap justify-center mt-2">
            {!isOwn && activeLive && (
              <button
                onClick={() => navigate(`/lives?start=${activeLive.id}`)}
                className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full text-sm font-bold bg-[#FF0050] text-white hover:opacity-90 transition animate-pulse"
                data-testid="join-live-btn"
              >
                <Radio className="w-4 h-4" /> {t.lives?.join ?? "Rejoindre le live"}
              </button>
            )}

            {!isOwn && me && (
              <button
                onClick={toggleFollow}
                disabled={followBusy}
                className={followStats.is_following
                  ? "px-5 py-2 rounded-full text-sm font-bold bg-white/10 text-white border border-white/15 hover:bg-white/15 transition disabled:opacity-60"
                  : "px-5 py-2 rounded-full text-sm font-bold bg-white text-black hover:opacity-90 transition disabled:opacity-60"}
                data-testid="follow-btn"
              >
                {followStats.is_following ? t.profile.following : t.profile.follow}
              </button>
            )}

            {!isOwn && isArtist && (
              <button
                onClick={() => setShowAgenda(true)}
                className="btn-secondary inline-flex items-center gap-2"
                data-testid="agenda-btn-visitor"
              >
                <Calendar className="w-4 h-4" /> {t.profile.events ?? "Agenda"}
              </button>
            )}

            {!isOwn && me && isArtist && (
              <button
                onClick={toggleReminder}
                disabled={reminderBusy}
                title={reminder.subscribed ? "Désactiver le rappel live" : "Me prévenir quand cet artiste démarre un live"}
                className={reminder.subscribed
                  ? "inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold bg-[#FF0050]/15 text-[#FF8DAB] border border-[#FF0050]/30 hover:bg-[#FF0050]/25 transition disabled:opacity-60"
                  : "inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold bg-white/5 text-white border border-white/10 hover:bg-white/10 transition disabled:opacity-60"}
                data-testid="live-reminder-btn"
              >
                {reminder.subscribed ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
                {reminder.subscribed ? "Rappel live ON" : "Rappel live"}
              </button>
            )}

            {isOwn && isArtist && (
              <>
                <Link to="/dashboard" className="btn-secondary inline-flex items-center gap-2" data-testid="dashboard-link">
                  <BarChart3 className="w-4 h-4" /> {t.dashboard?.goDashboard ?? "Tableau de bord"}
                </Link>
                <button onClick={() => setShowAgenda(true)} className="btn-secondary inline-flex items-center gap-2" data-testid="agenda-btn">
                  <Calendar className="w-4 h-4" /> {t.profile.events ?? "Agenda"}
                </button>
                <PublishMenu
                  open={publishOpen}
                  onToggle={() => setPublishOpen((v) => !v)}
                  onClose={() => setPublishOpen(false)}
                  onNewPhoto={() => { setPublishOpen(false); window.location.href = "/#composer"; }}
                  onNewTrack={() => { setPublishOpen(false); setShowTrackForm(true); }}
                  onNewProject={() => { setPublishOpen(false); setShowReleaseForm(true); }}
                  t={t}
                />
                {activeLive ? (
                  <button
                    onClick={async () => { await api.post(`/lives/${activeLive.id}/end`); loadAll(); toast.success(tr("Live ended")); }}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-bold bg-white/5 hover:bg-white/10 border border-white/10 text-zinc-200"
                    data-testid="stop-live-btn"
                  >
                    <X className="w-3.5 h-3.5" /> {t.profile.stopLive}
                  </button>
                ) : (
                  <button
                    onClick={() => setShowLiveForm(true)}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-bold bg-[#FF0050]/15 hover:bg-[#FF0050]/25 border border-[#FF0050]/50 text-[#FF0050]"
                    data-testid="go-live-btn"
                  >
                    <Radio className="w-3.5 h-3.5" /> {t.profile.goLive}
                  </button>
                )}
              </>
            )}

            {isOwn && !isArtist && (
              <button onClick={() => setShowAgenda(true)} className="btn-secondary inline-flex items-center gap-2" data-testid="agenda-btn-fan">
                <Calendar className="w-4 h-4" /> {t.profile.events ?? "Agenda"}
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-6 flex gap-1 border-b border-white/5 overflow-x-auto scroll-hide justify-center">
          {isArtist ? (
            <>
              <Tab label={t.profile.posts} active={tab === "posts"} onClick={() => setTab("posts")} testid="tab-posts" count={posts.length} />
              <Tab label={t.profile.photos} active={tab === "photos"} onClick={() => setTab("photos")} testid="tab-photos" count={posts.filter(p => p.image).length} />
              <Tab label={t.profile.discography} active={tab === "discography"} onClick={() => setTab("discography")} testid="tab-discography" count={tracks.length} />
            </>
          ) : (
            <>
              <Tab label={t.profile.news ?? "Actualité"} active={tab === "posts"} onClick={() => setTab("posts")} testid="tab-news" count={posts.length} />
              <Tab label={t.nav.challenges ?? "Défis"} active={tab === "challenges"} onClick={() => setTab("challenges")} testid="tab-challenges" count={0} />
            </>
          )}
        </div>

        {/* Content */}
        <div className="py-6">
          <ProfileTabBody
            tab={tab}
            t={t}
            isArtist={isArtist}
            posts={posts}
            tracks={tracks}
            onChange={loadAll}
            onBuy={setBuyTrack}
          />
        </div>
      </div>

      {/* Modals */}
      {editing && (
        <Modal onClose={() => setEditing(false)} title={t.profile.edit} testid="edit-modal">
          <div className="space-y-3">
            <input className="input-base" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} placeholder={t.auth.name} data-testid="edit-name" />
            <textarea className="input-base" rows={3} value={edit.bio} onChange={(e) => setEdit({ ...edit, bio: e.target.value })} placeholder={t.profile.bio} data-testid="edit-bio" />
            <button onClick={saveProfile} className="btn-primary w-full" data-testid="edit-save">{t.profile.save}</button>
            <ChangePasswordPanel />
            <BlockedUsersPanel className="!bg-transparent" />
            <div className="pt-3 border-t border-white/5 flex justify-center">
              <DeleteAccountPanel />
            </div>
          </div>
        </Modal>
      )}

      {showTrackForm && (
        <TrackForm releases={releases.filter((r) => !r.published)} onClose={() => setShowTrackForm(false)} onCreated={() => { setShowTrackForm(false); loadAll(); }} />
      )}

      {showReleaseForm && (
        <ReleaseForm onClose={() => setShowReleaseForm(false)} onCreated={() => { setShowReleaseForm(false); loadAll(); setShowTrackForm(true); }} />
      )}

      {showLiveForm && (
        <LiveForm onClose={() => setShowLiveForm(false)} onStarted={() => { setShowLiveForm(false); loadAll(); }} />
      )}

      {showAgenda && (
        <AgendaModal
          onClose={() => setShowAgenda(false)}
          events={events}
          isOwnArtist={isOwn && isArtist}
          onReload={loadAll}
          t={t}
        />
      )}

      {buyTrack && (
        <PurchaseModal track={buyTrack} onClose={() => setBuyTrack(null)} onSuccess={() => loadAll()} />
      )}

      {/* Suggestions visible at bottom of artist profiles */}
      {isArtist && (
        <div className="mt-10 px-4 sm:px-6 pb-6">
          <SuggestedArtists endpoint={`/users/${id}/similar`} title={t.discover?.similarArtists ?? "Tu pourrais aussi aimer"} limit={6} />
        </div>
      )}

      {savingMedia && (
        <div className="fixed inset-0 z-[70] grid place-items-center bg-black/50" data-testid="media-saving">
          <Loader2 className="w-7 h-7 text-[#FF0050] animate-spin" />
        </div>
      )}
    </div>
  );
}

function PublishMenu({ open, onToggle, onClose, onNewPhoto, onNewTrack, onNewProject, t }) {
  return (
    <div className="relative">
      <button
        onClick={onToggle}
        className="btn-primary inline-flex items-center gap-2"
        data-testid="publish-menu-btn"
      >
        <Plus className="w-4 h-4" /> {t.feed?.publish ?? "Publier"} <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={onClose} />
          <div className="absolute right-0 top-full mt-2 z-50 w-56 rounded-2xl bg-zinc-950 border border-white/10 shadow-2xl overflow-hidden anim-in" data-testid="publish-menu">
            <button onClick={onNewPhoto} className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-white hover:bg-white/5 transition" data-testid="menu-new-photo">
              <ImageIcon className="w-4 h-4 text-[#FFD60A]" />
              <span>{t.profile.newPhoto ?? "Photo"}</span>
            </button>
            <button onClick={onNewTrack} className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-white hover:bg-white/5 transition" data-testid="menu-new-track">
              <Music className="w-4 h-4 text-[#FF0050]" />
              <span>{t.profile.newSong ?? "Chanson"}</span>
            </button>
            <button onClick={onNewProject} className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-white hover:bg-white/5 transition" data-testid="menu-new-project">
              <Trophy className="w-4 h-4 text-[#00F2FE]" />
              <span>Album / EP / Maxi</span>
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function Tab({ label, active, onClick, count, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-4 py-3 text-sm font-semibold transition relative ${active ? "text-white" : "text-zinc-500 hover:text-zinc-300"}`}
    >
      {label} <span className="text-xs text-zinc-600">{count}</span>
      {active && <span className="absolute inset-x-3 bottom-0 h-0.5 bg-gradient-to-r from-[#FF0050] to-[#00F2FE] rounded-full" />}
    </button>
  );
}

function ProfileTabBody({ tab, t, isArtist, posts, tracks, onChange, onBuy }) {
  if (tab === "posts") {
    if (posts.length === 0) return <p className="text-zinc-500 text-center py-12">{t.feed.empty}</p>;
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        {posts.map((p) => <PostCard key={p.id} post={p} onChange={onChange} onBuy={onBuy} />)}
      </div>
    );
  }
  if (tab === "photos") {
    const photos = posts.filter((p) => p.image);
    if (photos.length === 0) return <p className="text-zinc-500 text-center py-12">{t.profile.noPhotos}</p>;
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-3" data-testid="photos-grid">
        {photos.map((p) => (
          <div key={p.id} className="aspect-square overflow-hidden rounded-xl bg-zinc-900 group">
            <img src={resolveImg(p.image)} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
          </div>
        ))}
      </div>
    );
  }
  if (tab === "discography" && isArtist) {
    return <DiscographySection tracks={tracks} releases={releases} t={t} onBuy={onBuy} isOwn={isOwn} onReload={loadAll} />;
  }
  if (tab === "challenges") {
    return (
      <p className="text-zinc-500 text-center py-12" data-testid="profile-challenges-empty">
        {t.profile.noChallenges ?? "Aucun défi participé pour l'instant."}
      </p>
    );
  }
  return null;
}

function DiscographySection({ tracks, releases = [], t, onBuy, isOwn = false, onReload }) {
  const [sub, setSub] = useState("single");
  const [q, setQ] = useState("");
  const subs = [
    { key: "single", label: t.profile.singles },
    { key: "album",  label: t.profile.albums },
    { key: "ep",     label: t.profile.eps },
    { key: "maxi",   label: t.profile.maxis },
  ];
  // A song that belongs to a project is shown inside the project, not on its own.
  const standalone = (type) => tracks.filter((tr) => (tr.release_type || "single") === type && (!tr.release_id || type === "single"));
  const projectsOf = (type) => releases.filter((rl) => rl.release_type === type);
  const countOf = (type) => (type === "single" ? standalone(type).length : standalone(type).length + projectsOf(type).length);
  const filtered = standalone(sub).filter((tr) => tr.title.toLowerCase().includes(q.toLowerCase()));
  const shownReleases = sub === "single" ? [] : projectsOf(sub).filter((rl) => rl.title.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2 justify-center">
        {subs.map((s) => (
          <button
            key={s.key}
            onClick={() => setSub(s.key)}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition ${sub === s.key ? "bg-white text-black" : "bg-zinc-900 text-zinc-300 hover:bg-zinc-800 border border-white/5"}`}
            data-testid={`disco-sub-${s.key}`}
          >
            {s.label} <span className="text-xs opacity-60">{countOf(s.key)}</span>
          </button>
        ))}
      </div>
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={t.profile.searchTrack}
        className="input-base"
        data-testid="disco-search"
      />
      {filtered.length === 0 && shownReleases.length === 0 ? (
        <p className="text-zinc-500 text-center py-12">{t.profile.noReleases}</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
          {shownReleases.map((rl) => <ReleaseCard key={rl.id} release={rl} />)}
          {filtered.map((tr) => (
            <div key={tr.id}>
              <TrackCard track={tr} queue={filtered} onBuy={onBuy} />
              {isOwn && <SongPriceEdit track={tr} onDone={onReload} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Artist-only: change the price of one of my songs (past purchases never change; inside a
 *  published project the server refuses a price that would break the project's price rules). */
function SongPriceEdit({ track, onDone }) {
  const [v, setV] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    const price = parseFloat(v);
    if (!(price >= 0)) return;
    setBusy(true);
    try {
      await api.patch(`/tracks/${track.id}/price`, { price });
      toast.success("Prix mis à jour.");
      setV("");
      onDone && onDone();
    } catch (e2) { toast.error(e2.response?.data?.detail || "Impossible de changer le prix"); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} className="mt-2 flex gap-1" data-testid={`song-price-edit-${track.id}`}>
      <input type="number" step="0.01" min="0" value={v} onChange={(e) => setV(e.target.value)} placeholder="Prix $ (0 = gratuit, sinon 0,25 min.)" className="input-base text-xs py-1.5 flex-1 min-w-0" />
      <button disabled={busy || !v} className="btn-secondary text-xs px-3 disabled:opacity-50">OK</button>
    </form>
  );
}

function AgendaModal({ onClose, events, isOwnArtist, onReload, t }) {
  const { user: me } = useAuth();
  const [show, setShow] = useState(false);
  const [data, setData] = useState({ title: "", location: "", date: "", description: "", type: "concert" });
  const [busy, setBusy] = useState(false);
  const [interested, setInterested] = useState({}); // event_id -> bool

  useEffect(() => {
    if (!me || isOwnArtist) return;
    (async () => {
      const entries = await Promise.all(events.map(async (e) => {
        try { const { data: d } = await api.get(`/events/${e.id}/interest`); return [e.id, d.interested]; }
        catch { return [e.id, false]; }
      }));
      setInterested(Object.fromEntries(entries));
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, isOwnArtist]);

  const toggleInterest = async (eventId) => {
    if (!me) { toast.error(tr("Sign in to interact")); return; }
    try {
      const { data: d } = await api.post(`/events/${eventId}/interest`);
      setInterested((s) => ({ ...s, [eventId]: d.interested }));
      toast.success(d.interested ? tr("Ajouté à mon agenda") : tr("Retiré de mon agenda"));
    } catch (e) { toast.error(tr(e.response?.data?.detail || "Could not save")); }
  };

  const create = async () => {
    if (!data.title.trim() || !data.date.trim()) return toast.error(tr("Title and date are required"));
    setBusy(true);
    try {
      await api.post("/events", data);
      setData({ title: "", location: "", date: "", description: "", type: "concert" });
      setShow(false);
      onReload?.();
      toast.success(tr("Event saved"));
    } catch (e) { toast.error(tr(e.response?.data?.detail || "Could not save")); }
    finally { setBusy(false); }
  };

  return (
    <Modal onClose={onClose} title={t.profile.events ?? "Agenda"} testid="agenda-modal">
      {isOwnArtist && !show && (
        <button onClick={() => setShow(true)} className="btn-secondary w-full inline-flex items-center justify-center gap-2 mb-3" data-testid="agenda-new">
          <Plus className="w-4 h-4" /> {t.agenda?.add ?? "Ajouter un événement"}
        </button>
      )}
      {isOwnArtist && show && (
        <div className="space-y-2 mb-3 p-3 bg-white/5 border border-white/5 rounded-xl">
          <input className="input-base" value={data.title} onChange={(e) => setData({ ...data, title: e.target.value })} placeholder={t.agenda?.name ?? "Titre"} data-testid="agenda-title" />
          <input className="input-base" value={data.location} onChange={(e) => setData({ ...data, location: e.target.value })} placeholder={t.agenda?.location ?? "Lieu"} data-testid="agenda-location" />
          <input type="datetime-local" className="input-base" value={data.date} onChange={(e) => setData({ ...data, date: e.target.value })} data-testid="agenda-date" />
          <textarea rows={2} className="input-base" value={data.description} onChange={(e) => setData({ ...data, description: e.target.value })} placeholder={t.agenda?.desc ?? "Description"} data-testid="agenda-desc" />
          <button onClick={create} disabled={busy} className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="agenda-save">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}{t.profile.save}
          </button>
        </div>
      )}
      {events.length === 0 ? (
        <p className="text-zinc-500 text-center py-6">{t.agenda?.empty ?? "Aucun événement"}</p>
      ) : (
        <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1">
          {events.map((e) => (
            <div key={e.id} className="card-flat p-3 flex items-center gap-3" data-testid={`event-${e.id}`}>
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#FF0050] to-[#8A2BE2] grid place-items-center text-white">
                <Calendar className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-semibold truncate text-sm">{e.title}</p>
                <p className="text-xs text-zinc-400 truncate">{e.location} · {new Date(e.date).toLocaleString()}</p>
              </div>
              <span className="chip bg-white/5 text-zinc-300 border border-white/5 capitalize text-[10px]">{e.type}</span>
              {!isOwnArtist && (
                <button
                  onClick={() => toggleInterest(e.id)}
                  title={interested[e.id] ? "Retirer de mon agenda" : "Ajouter à mon agenda"}
                  className={`p-2 rounded-full transition shrink-0 ${interested[e.id] ? "bg-[#00F2FE]/15 text-[#00F2FE]" : "bg-white/5 text-zinc-400 hover:text-white"}`}
                  data-testid={`event-interest-${e.id}`}
                >
                  {interested[e.id] ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

function Modal({ children, onClose, title, testid }) {
  return (
    <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-md grid place-items-center p-4" data-testid={testid}>
      <div className="w-full max-w-md bg-zinc-950 border border-white/10 rounded-3xl p-6 anim-in relative">
        <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full bg-white/5 hover:bg-white/10" aria-label="close"><X className="w-4 h-4 text-zinc-400" /></button>
        <h3 className="text-2xl font-black font-display text-white mb-4">{title}</h3>
        {children}
      </div>
    </div>
  );
}

function TrackForm({ onClose, onCreated, releases = [] }) {
  const { t } = useI18n();
  const [data, setData] = useState({
    title: "", cover: "", audio_url: "", release_id: "",
    price: 0.99, genre: "Pop", release_type: "single",
    audio_format: "mp3", preview_start: 0, preview_duration: 30, is_free: false,
    feat: "", producers: "", authors: "", composers: "", label: "",
  });
  const [busy, setBusy] = useState(false);

  const csvToArr = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);
  const release = releases.find((rl) => rl.id === data.release_id) || null;
  const pickRelease = (rid) => {
    const rl = releases.find((x) => x.id === rid);
    setData(rl ? { ...data, release_id: rid, release_type: rl.release_type, cover: rl.cover, genre: rl.genre || data.genre } : { ...data, release_id: "", cover: "" });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!data.audio_url) { toast.error("Ajoute d'abord le fichier audio de la chanson."); return; }
    if (!data.cover) { toast.error("Ajoute une pochette (ou choisis un projet qui en a une)."); return; }
    setBusy(true);
    try {
      await api.post("/tracks", {
        ...data,
        release_id: data.release_id || null,
        price: parseFloat(data.price) || 0,
        preview_start: parseInt(data.preview_start, 10) || 0,
        preview_duration: parseInt(data.preview_duration, 10) || 30,
        feat: csvToArr(data.feat),
        producers: csvToArr(data.producers),
        authors: csvToArr(data.authors),
        composers: csvToArr(data.composers),
      });
      toast.success(tr("Track published"));
      // a song inside a draft project goes public with the project (counted then)
      if (!data.release_id) trackPublish("track");
      onCreated();
    } catch { toast.error(tr("Could not publish")); }
    finally { setBusy(false); }
  };

  return (
    <Modal onClose={onClose} title={t.profile.newTrack} testid="track-modal">
      <form onSubmit={submit} className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
        <input required className="input-base" value={data.title} onChange={(e) => setData({ ...data, title: e.target.value })} placeholder={t.profile.title2} data-testid="track-title-input" />
        {releases.length > 0 && (
          <label className="block">
            <span className="text-[11px] uppercase tracking-wider text-zinc-400 font-bold">Projet (optionnel)</span>
            <select className="input-base mt-1" value={data.release_id} onChange={(e) => pickRelease(e.target.value)} data-testid="track-release-select">
              <option value="">Aucun — chanson seule (single)</option>
              {releases.map((rl) => <option key={rl.id} value={rl.id}>{rl.title} ({rl.release_type.toUpperCase()} · {rl.tracks_count} titre{rl.tracks_count > 1 ? "s" : ""})</option>)}
            </select>
          </label>
        )}
        {release ? (
          <p className="text-xs text-zinc-400 flex items-center gap-2" data-testid="track-uses-release-cover">
            <img src={resolveImg(release.cover)} alt="" className="w-10 h-10 rounded object-cover" /> Cette chanson aura la pochette de « {release.title} ».
          </p>
        ) : (
          <FileUploader
            accept="image/jpeg,image/png,image/webp,image/heic,image/heif,image/*"
            label={t.profile.uploadCover}
            dataTestid="track-cover-upload"
            preview={data.cover}
            onUploaded={(url) => setData({ ...data, cover: url })}
            onClear={() => setData({ ...data, cover: "" })}
          />
        )}
        <FileUploader
          accept="audio/mpeg,audio/wav,audio/mp3,audio/*"
          label={t.profile.uploadAudio}
          dataTestid="track-audio-upload"
          preview={data.audio_url}
          onUploaded={(url) => setData({ ...data, audio_url: url })}
          onClear={() => setData({ ...data, audio_url: "" })}
        />
        <div className="grid grid-cols-2 gap-2">
          <select className="input-base disabled:opacity-50" disabled={!!release} value={data.release_type} onChange={(e) => setData({ ...data, release_type: e.target.value })} data-testid="track-release-type">
            <option value="single">{t.profile.singles}</option>
            <option value="album">{t.profile.albums}</option>
            <option value="ep">{t.profile.eps}</option>
            <option value="maxi">{t.profile.maxis}</option>
          </select>
          <select className="input-base" value={data.audio_format} onChange={(e) => setData({ ...data, audio_format: e.target.value })} data-testid="track-audio-format">
            <option value="mp3">MP3</option>
            <option value="wav">WAV</option>
          </select>
        </div>
        <label className="flex items-center gap-2 text-sm text-zinc-300">
          <input type="checkbox" checked={data.is_free} onChange={(e) => setData({ ...data, is_free: e.target.checked })} data-testid="track-is-free" className="accent-[#FF0050]" />
          {t.profile.freeTrack}
        </label>
        <div className="grid grid-cols-2 gap-2">
          <input required={!data.is_free} disabled={data.is_free} type="number" step="0.01" min="0.25" className="input-base disabled:opacity-50" value={data.price} onChange={(e) => setData({ ...data, price: e.target.value })} placeholder={`${t.profile.price} — 0,25 $ minimum`} data-testid="track-price-input" />
          <input required className="input-base" value={data.genre} onChange={(e) => setData({ ...data, genre: e.target.value })} placeholder={t.profile.genre} data-testid="track-genre-input" />
        </div>
        {!data.is_free && <PriceEquivalents usd={data.price} className="!mt-0" />}
        <div className="grid grid-cols-2 gap-2">
          <input type="number" min="0" className="input-base" value={data.preview_start} onChange={(e) => setData({ ...data, preview_start: e.target.value })} placeholder={t.profile.previewStart} data-testid="track-preview-start" />
          <input type="number" min="10" max="60" className="input-base" value={data.preview_duration} onChange={(e) => setData({ ...data, preview_duration: e.target.value })} placeholder={t.profile.previewDuration} data-testid="track-preview-duration" />
        </div>
        <p className="text-xs font-bold uppercase tracking-wider text-zinc-500 mt-3">{t.profile.creditsTitle}</p>
        <input className="input-base" value={data.feat} onChange={(e) => setData({ ...data, feat: e.target.value })} placeholder={t.profile.featPh} data-testid="track-feat" />
        <input className="input-base" value={data.producers} onChange={(e) => setData({ ...data, producers: e.target.value })} placeholder={t.profile.prodPh} data-testid="track-prod" />
        <input className="input-base" value={data.authors} onChange={(e) => setData({ ...data, authors: e.target.value })} placeholder={t.profile.authorPh} data-testid="track-author" />
        <input className="input-base" value={data.composers} onChange={(e) => setData({ ...data, composers: e.target.value })} placeholder={t.profile.composerPh} data-testid="track-composer" />
        <input className="input-base" value={data.label} onChange={(e) => setData({ ...data, label: e.target.value })} placeholder={t.profile.labelPh} data-testid="track-label" />
        <button type="submit" disabled={busy} className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="track-create-btn">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />}{t.profile.save}
        </button>
      </form>
    </Modal>
  );
}

function ReleaseForm({ onClose, onCreated }) {
  const [d, setD] = useState({ title: "", release_type: "album", cover: "", genre: "Pop", description: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!d.cover) { toast.error("Ajoute la pochette du projet."); return; }
    setBusy(true);
    try {
      await api.post("/releases", d);
      toast.success("Projet créé. Ajoute maintenant tes chansons, une par une.");
      onCreated();
    } catch (e2) { toast.error(e2.response?.data?.detail || "Impossible de créer le projet"); }
    finally { setBusy(false); }
  };
  return (
    <Modal onClose={onClose} title="Nouveau projet" testid="release-modal">
      <form onSubmit={submit} className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
        <input required maxLength={120} className="input-base" value={d.title} onChange={(e) => setD({ ...d, title: e.target.value })} placeholder="Nom du projet" data-testid="release-title-input" />
        <div className="grid grid-cols-2 gap-2">
          <select className="input-base" value={d.release_type} onChange={(e) => setD({ ...d, release_type: e.target.value })} data-testid="release-type-select">
            <option value="album">Album</option>
            <option value="ep">EP</option>
            <option value="maxi">Maxi</option>
          </select>
          <input className="input-base" value={d.genre} onChange={(e) => setD({ ...d, genre: e.target.value })} placeholder="Genre" data-testid="release-genre-input" />
        </div>
        <FileUploader
          accept="image/jpeg,image/png,image/webp,image/heic,image/heif,image/*"
          label="Pochette du projet (la même pour toutes les chansons)"
          dataTestid="release-cover-upload"
          preview={d.cover}
          onUploaded={(url) => setD({ ...d, cover: url })}
          onClear={() => setD({ ...d, cover: "" })}
        />
        <textarea rows={3} maxLength={1000} className="input-base" value={d.description} onChange={(e) => setD({ ...d, description: e.target.value })} placeholder="Description (optionnel)" data-testid="release-description-input" />
        <button type="submit" disabled={busy} className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="release-create-btn">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Créer le projet
        </button>
      </form>
    </Modal>
  );
}

function LiveForm({ onClose, onStarted }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data: live } = await api.post("/lives/start", { title });
      toast.success(tr("You are live!"));
      onStarted();
      // SPA navigation so the LiveRoom opens and the camera prompt fires immediately.
      navigate(`/lives?start=${live.id}`);
    } catch { toast.error(tr("Could not start live")); }
    finally { setBusy(false); }
  };
  return (
    <Modal onClose={onClose} title={t.lives.goLive} testid="live-modal">
      <form onSubmit={submit} className="space-y-3">
        <input required className="input-base" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t.lives.enterTitle} data-testid="live-title-input" />
        <button type="submit" disabled={busy} className="btn-live w-full inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="live-start-btn">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />}{t.lives.start}
        </button>
      </form>
    </Modal>
  );
}
